/**
 * Ce qu'on dit d'un stage à un moteur, et comment on le dit.
 *
 * Le cahier des charges du 11 septembre 2026 pose le critère : une IA doit
 * pouvoir répondre avec certitude à QUI, QUOI, OÙ, QUAND, POUR QUI, À QUEL
 * PRIX, PEUT-ON ENCORE S'INSCRIRE. Ce module produit les phrases et le JSON-LD
 * qui portent ces réponses, à partir de la seule base.
 *
 * ## Les bornes d'âge sont EXCLUES en haut
 *
 * `age_max` est une borne exclue : la production teste `age >= age_min AND
 * age < age_max` des deux côtés, catalogue parent et inscription
 * administrative. « Les Hirondelles 6 - 8 ans » porte donc 6 et 9.
 *
 * Les cartes du catalogue affichent pourtant `age_min - age_max` tel quel,
 * c'est-à-dire « 6 - 9 ans » pour un stage de 6 à 8. Une page indexée doit
 * dire la vérité : on affiche la borne haute RÉELLE, `age_max - 1`, et on la
 * calcule ici plutôt que dans chaque composant.
 */

export interface StagePublic {
  slug: string;
  start_date: string;
  end_date: string;
  nombre_jours: number | null;
  remarques: string | null;
  /*
    L'instant ou les inscriptions ouvrent, en UTC. `null` veut dire « rien a
    attendre » — 391 sessions de production n'en portent aucun.
  */
  visible_from: string | null;
  places: number;
  places_restantes: number;
  prix_normal: number;
  prix_reduit: number | null;
  prix_local: number | null;
  prix_local_reduit: number | null;
  stage: string;
  type_activite: TypeDActivite;
  description: string | null;
  age_min: number;
  age_max: number;
  image_url: string | null;
  centre: string;
  address: string | null;
  address2: string | null;
  postal_code: string | null;
  city: string | null;
  periode_code: string | null;
  periode: string | null;
  annee: number | null;
  semaine: string | null;
}

export const ORGANISATEUR = 'Éclosion ASBL';

/** Le nom du site, distinct de celui de l'association qui le tient. */
export const NOM_DU_SITE = 'Co-nest';

/**
 * Les trois formes d'activité, telles que l'association les distingue.
 *
 * > « Les aigles, les hirondelles, les pinsons, les poussins et les colibris,
 * >   ce sont des plaines de vacances, des activités variées. Les stages, ce
 * >   sont des semaines à thème. Les séjours, ce sont des activités où on dort
 * >   sur place. »
 *
 * La distinction compte pour le référencement local : « plaine de vacances
 * Bruxelles » et « stage enfant Bruxelles » ne sont pas la même recherche, et
 * une page qui ne dit pas laquelle des deux elle est ne répond ni à l'une ni à
 * l'autre.
 */
export type TypeDActivite = 'plaine' | 'stage' | 'sejour';

/** Au singulier, pour une phrase : « Cette plaine de vacances… ». */
export const NOM_DU_TYPE: Record<TypeDActivite, string> = {
  plaine: 'plaine de vacances',
  stage: 'stage',
  sejour: 'séjour',
};

/** Au pluriel, pour un titre de section. */
export const NOM_DU_TYPE_PLURIEL: Record<TypeDActivite, string> = {
  plaine: 'Plaines de vacances',
  stage: 'Stages à thème',
  sejour: 'Séjours',
};

/**
 * Le genre grammatical de chaque type — une plaine, un stage, un sejour.
 *
 * Il vit ici, avec les noms, parce que c'est la meme connaissance : qui ajoute
 * un type ajoute son genre dans la foulee. Enfoui dans une comparaison
 * (`typeDe(s) === 'plaine'`), il s'etait deja duplique dans deux fonctions, et
 * chaque nouvelle phrase a accorder en aurait fait une troisieme.
 */
export const GENRE_DU_TYPE: Record<TypeDActivite, 'f' | 'm'> = {
  plaine: 'f',
  stage: 'm',
  sejour: 'm',
};

/**
 * Ce qui distingue ce type, dit à une famille — et à une IA.
 *
 * Un mot seul ne suffit pas : « plaine » ne dit rien à qui ne connaît pas le
 * vocabulaire belge de l'accueil extrascolaire.
 */
export const CE_QU_EST_LE_TYPE: Record<TypeDActivite, string> = {
  plaine:
    'Une plaine de vacances propose des activités variées tout au long de la semaine : sport, arts, cuisine, sorties, psychomotricité.',
  stage: 'Un stage est une semaine consacrée à un seul thème.',
  sejour: 'Un séjour est une activité avec nuitées : les enfants dorment sur place.',
};

/** Le type d'un stage, avec un repli sûr si la base en portait un inconnu. */
export function typeDe(s: Pick<StagePublic, 'type_activite'>): TypeDActivite {
  return s.type_activite in NOM_DU_TYPE ? s.type_activite : 'stage';
}

/**
 * La forme du JSON-LD qu'on produit.
 *
 * Declaree plutot que laissee a `object` : ces structures sont lues par des
 * moteurs qui ne pardonnent pas un champ mal nomme, et un test qui doit ecrire
 * `as any` pour les relire ne verifie plus rien de leur forme.
 */
export interface OffreJsonLd {
  '@type': 'Offer';
  name: string;
  price: string;
  priceCurrency: 'EUR';
  availability: string;
  /* L'instant ou l'offre devient valable : l'ouverture des inscriptions. */
  validFrom?: string;
  url: string;
}

export interface AdresseJsonLd {
  '@type': 'PostalAddress';
  streetAddress?: string;
  postalCode?: string;
  addressLocality: string;
  addressCountry: 'BE';
}

export interface EvenementJsonLd {
  '@context': 'https://schema.org';
  '@type': 'Event';
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  eventStatus: string;
  eventAttendanceMode: string;
  location: { '@type': 'Place'; name: string; address: AdresseJsonLd };
  organizer: { '@type': 'Organization'; name: string; url: string };
  audience: { '@type': 'PeopleAudience'; suggestedMinAge: number; suggestedMaxAge: number };
  image?: string;
  url: string;
  offers?: OffreJsonLd[];
  isAccessibleForFree: boolean;
  inLanguage: string;
}

export interface FilDArianeJsonLd {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: { '@type': 'ListItem'; position: number; name: string; item: string }[];
}

/** « 3,5 » et non « 3.5 » : c'est un âge lu par des familles francophones. */
function nombreFr(n: number): string {
  return String(n).replace('.', ',');
}

/**
 * « de 6 à 8 ans » — la borne haute réelle, `age_max` étant exclue.
 *
 * Un stage 6 → 9 accueille les enfants de 6 ans à 8 ans et 11 mois. Écrire
 * « 6 à 9 ans » attirerait des familles d'enfants de 9 ans que l'inscription
 * refuserait ensuite.
 */
export function trancheDAge(ageMin: number, ageMax: number): string {
  const haut = ageMax - 1;
  if (haut <= ageMin) return `${nombreFr(ageMin)} ans`;
  return `${nombreFr(ageMin)} à ${nombreFr(haut)} ans`;
}

/** « du 19 au 23 octobre 2026 », ou « le 19 octobre 2026 » sur un seul jour. */
/**
 * « 1er » et non « 1 » : le seul jour du mois qui s'ecrit en ordinal.
 *
 * `Intl` rend « 1 decembre », ce qui n'est pas du francais. La reparation est
 * ici, en un endroit, plutot que dans chaque fonction qui met une date en
 * toutes lettres — il y en a deux, ce qui suffit a ce qu'elles divergent.
 */
export function premierDuMois(date: string): string {
  // « 1 » suivi d'une espace ou en fin de texte : « lundi 1 » (sans le mois) aussi.
  return date.replace(/(^|\s)1(?=\s|$)/, (_, avant: string) => `${avant}1er`);
}

export function periodeEnToutesLettres(debut: string, fin: string): string {
  const d = new Date(`${debut}T12:00:00Z`);
  const f = new Date(`${fin}T12:00:00Z`);
  const jour = (x: Date) =>
    premierDuMois(
      new Intl.DateTimeFormat('fr-BE', { day: 'numeric', timeZone: 'UTC' }).format(x),
    );
  const complet = (x: Date) =>
    premierDuMois(
      new Intl.DateTimeFormat('fr-BE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(x),
    );

  if (debut === fin) return `le ${complet(d)}`;

  const memeMois =
    d.getUTCMonth() === f.getUTCMonth() && d.getUTCFullYear() === f.getUTCFullYear();
  return memeMois ? `du ${jour(d)} au ${complet(f)}` : `du ${complet(d)} au ${complet(f)}`;
}

/**
 * Les inscriptions sont-elles ouvertes ?
 *
 * `maintenant` est un parametre plutot qu'un `new Date()` cache : une fonction
 * qui lit l'horloge ne s'eprouve qu'en attendant, et le pre-rendu au build a
 * besoin de savoir ce que la page dira, pas ce qu'elle dit a la seconde ou on
 * la compile.
 */
export function inscriptionsOuvertes(
  /*
    Le minimum necessaire, et non `StagePublic` entier : le catalogue connecte
    manipule une autre forme de session, qui porte la meme colonne. Exiger le
    type complet aurait fait recopier la regle la-bas.
  */
  s: { visible_from: string | null },
  maintenant = new Date(),
): boolean {
  if (!s.visible_from) return true;
  const ouverture = new Date(s.visible_from);
  if (Number.isNaN(ouverture.getTime())) return true;
  return ouverture <= maintenant;
}

/**
 * « le mardi 15 septembre 2026 à 10h00 », a l'heure de Bruxelles.
 *
 * La base stocke un instant UTC. Une famille qui lirait « 08h00 » se
 * presenterait deux heures trop tot — et le jour meme, deux heures comptent.
 * `Intl` connait les regles de passage a l'heure d'ete ; une soustraction
 * d'une ou deux heures, non.
 *
 * Le jour de la semaine figure parce que c'est ainsi qu'on retient un
 * rendez-vous : « mardi prochain », pas « le quinze ».
 */
export function momentDOuverture(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const jour = premierDuMois(
    new Intl.DateTimeFormat('fr-BE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/Brussels',
    }).format(d),
  );

  const heure = new Intl.DateTimeFormat('fr-BE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Brussels',
  })
    .format(d)
    .replace(':', 'h');

  return `le ${jour} à ${heure}`;
}

/**
 * Ce que la page dit d'un stage qui n'est pas encore ouvert.
 *
 * Rendue vide quand il l'est : l'appelant n'a alors rien a afficher, et c'est
 * plus sur qu'un booleen qu'il faudrait penser a consulter.
 */
export function phraseDOuverture(s: StagePublic, maintenant = new Date()): string {
  if (inscriptionsOuvertes(s, maintenant) || !s.visible_from) return '';
  const moment = momentDOuverture(s.visible_from);
  if (!moment) return '';
  return `Les inscriptions ouvrent ${moment}.`;
}

/** La commune, pour les titres : `city` quand elle existe, sinon le centre. */
export function commune(s: Pick<StagePublic, 'city' | 'centre'>): string {
  return (s.city ?? '').trim() || s.centre;
}

/**
 * Le `<title>`.
 *
 * Format : « Stage <nom> <âge> à <commune> — <période> <année> | Éclosion ».
 * L'unicité vient du quadruplet (stage, commune, période, année), qui est
 * exactement ce que le slug porte — donc deux pages distinctes ont deux titres
 * distincts, par construction.
 */
export function titreDuStage(s: StagePublic): string {
  const quand = [s.periode, s.annee].filter(Boolean).join(' ');
  const debut = `${s.stage} ${trancheDAge(s.age_min, s.age_max)} à ${commune(s)}`;
  return quand ? `${debut} — ${quand} | Éclosion` : `${debut} | Éclosion`;
}

/**
 * Le mot juste pour designer cette activite dans une phrase.
 *
 * « Ce stage » pour une semaine a theme, « Cette plaine de vacances » pour un
 * groupe. Dire « stage » de tout, c'est se priver de la moitie des recherches.
 */
export function articleEtType(s: StagePublic, majuscule = false): string {
  const t = typeDe(s);
  const article = GENRE_DU_TYPE[t] === 'f' ? 'Cette' : 'Ce';
  const mot = `${majuscule ? article : article.toLowerCase()} ${NOM_DU_TYPE[t]}`;
  return mot;
}

/**
 * La méta-description.
 *
 * Elle reprend les faits plutôt qu'un slogan : c'est ce qu'un moteur cite, et
 * ce qu'une IA relit pour répondre. Bornée à ~160 caractères utiles.
 */
export function descriptionDuStage(s: StagePublic): string {
  const phrases = [
    `${NOM_DU_TYPE[typeDe(s)].replace(/^./, (c) => c.toUpperCase())} ${s.stage} pour enfants de ${trancheDAge(s.age_min, s.age_max)} à ${commune(s)}, ${periodeEnToutesLettres(s.start_date, s.end_date)}.`,
    `${prixEnEuros(s.prix_normal)} la semaine.`,
    s.places_restantes > 0 ? 'Places disponibles.' : 'Complet.',
    'Inscription en ligne avec Éclosion ASBL.',
  ];
  return phrases.join(' ');
}

/**
 * La meme information, en une ligne de liste.
 *
 * Le catalogue et les pages de commune l'ecrivaient chacun a leur facon, en
 * copie l'un de l'autre : deux endroits a corriger le jour ou la regle change,
 * et c'est ce jour-la. Elle est donc ici, avec la phrase longue qu'elle abrege.
 */
export function mentionDeDisponibilite(s: StagePublic, maintenant = new Date()): string {
  if (!inscriptionsOuvertes(s, maintenant) && s.visible_from) {
    const moment = momentDOuverture(s.visible_from);
    if (moment) return `Ouverture des inscriptions ${moment}.`;
  }
  if (s.places_restantes > 0) {
    return `Il reste ${s.places_restantes} place${s.places_restantes > 1 ? 's' : ''}.`;
  }
  return 'Complet.';
}

/**
 * « Il reste 11 places sur 24 », ou « … est complète ».
 *
 * Une seule definition, appelee par les phrases explicites ET par l'encadre de
 * la page. Elle etait ecrite deux fois : la page disait « Ce stage est
 * complet » la ou les phrases disaient « Cette plaine de vacances est
 * complète », donc la meme page se contredisait a dix lignes d'ecart.
 *
 * L'accord au feminin est porte par le mot entier — « complet » + « e » donne
 * « complete », sans accent, ce que le banc a trouve.
 */
export function phraseDeDisponibilite(s: StagePublic, maintenant = new Date()): string {
  /*
    Tant que ce n'est pas ouvert, le nombre de places ne veut rien dire : elles
    sont toutes libres, et l'annoncer inviterait a venir trop tot. La date
    d'ouverture est la seule reponse utile a « peut-on encore s'inscrire ».
  */
  if (!inscriptionsOuvertes(s, maintenant)) {
    return phraseDOuverture(s, maintenant);
  }
  if (s.places_restantes > 0) {
    const p = s.places_restantes > 1 ? 's' : '';
    return `Il reste ${s.places_restantes} place${p} disponible${p} sur ${s.places}.`;
  }
  const feminin = GENRE_DU_TYPE[typeDe(s)] === 'f';
  return `${articleEtType(s, true)} est ${feminin ? 'complète' : 'complet'} : les ${s.places} places sont prises.`;
}

export function prixEnEuros(montant: number | null | undefined): string {
  if (montant === null || montant === undefined || Number.isNaN(montant)) return '—';
  return `${montant.toFixed(2).replace('.', ',')} €`;
}

/**
 * Les phrases explicites que réclame le cahier des charges (§20).
 *
 * > « Éviter de dépendre uniquement d'éléments graphiques comme 👶 6-8,
 * >   📍 Saint-Hubert, 🕘 9-16, 💰 95. »
 *
 * Un pictogramme ne se lit pas. Ces phrases sont dans le HTML, en toutes
 * lettres, et ce sont elles qu'une IA peut citer telles quelles.
 */
export function phrasesDuStage(s: StagePublic): string[] {
  const t = typeDe(s);
  const phrases = [
    `${articleEtType(s, true)} est destiné${t === 'plaine' ? 'e' : ''} aux enfants de ${trancheDAge(s.age_min, s.age_max)}.`,
    CE_QU_EST_LE_TYPE[t],
    `Il est organisé par ${ORGANISATEUR} à ${commune(s)}${s.centre !== commune(s) ? `, au centre ${s.centre}` : ''}.`,
    `Il a lieu ${periodeEnToutesLettres(s.start_date, s.end_date)}${s.nombre_jours ? `, soit ${s.nombre_jours} jour${s.nombre_jours > 1 ? 's' : ''} d'activité` : ''}.`,
    `Le prix ${t === 'plaine' ? 'de la semaine' : 'du stage'} est de ${prixEnEuros(s.prix_normal)}.`,
  ];

  if (s.prix_reduit !== null) {
    phrases.push(
      `Un tarif réduit de ${prixEnEuros(s.prix_reduit)} est appliqué aux familles qui y ont droit.`,
    );
  }
  if (s.prix_local !== null) {
    phrases.push(
      `Un tarif local de ${prixEnEuros(s.prix_local)} est appliqué aux familles de la commune.`,
    );
  }

  phrases.push(phraseDeDisponibilite(s));

  if (s.remarques?.trim()) phrases.push(s.remarques.trim());

  return phrases;
}

/** L'adresse postale complète du centre, sur une ligne. */
export function adresseComplete(s: StagePublic): string {
  return [s.address, s.address2, [s.postal_code, s.city].filter(Boolean).join(' ')]
    .map((x) => (x ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * Le JSON-LD `Event`.
 *
 * Construit depuis la base et non saisi à la main : le cahier des charges
 * l'exige (§9), et pour une raison qui se vérifie — des données structurées
 * qui divergent du contenu affiché sont une raison de déclassement, pas un
 * détail. Les mêmes champs alimentent la page et ce bloc.
 *
 * `offers.availability` dit la vérité sur les places, ce qui n'était possible
 * qu'une fois `programme_places_restantes` écrite : la RLS de `registrations`
 * aurait autrement fait compter zéro inscrit à un visiteur.
 */
export function evenementJsonLd(
  s: StagePublic,
  origine: string,
  maintenant = new Date(),
): EvenementJsonLd {
  const url = `${origine}/stages/${s.slug}`;
  const ouvert = inscriptionsOuvertes(s, maintenant);

  const offres = [
    { nom: 'Tarif normal', prix: s.prix_normal },
    { nom: 'Tarif réduit', prix: s.prix_reduit },
    { nom: 'Tarif local', prix: s.prix_local },
    { nom: 'Tarif local réduit', prix: s.prix_local_reduit },
  ]
    .filter((o): o is { nom: string; prix: number } => o.prix !== null && o.prix !== undefined)
    .map((o): OffreJsonLd => ({
      '@type': 'Offer',
      name: o.nom,
      price: o.prix.toFixed(2),
      priceCurrency: 'EUR',
      /*
        `PreOrder` est le mot de schema.org pour « annonce, pas encore en
        vente ». Dire `InStock` d'un stage qui n'ouvre que dans trois jours
        ferait promettre au moteur une inscription que la base refuse — et
        c'est exactement le genre d'ecart qui fait perdre l'affichage enrichi.
      */
      availability: !ouvert
        ? 'https://schema.org/PreOrder'
        : s.places_restantes > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/SoldOut',
      validFrom: s.visible_from ?? undefined,
      url,
    }));

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: `${s.stage} — ${commune(s)}`,
    description:
      s.description?.trim() ||
      `Stage pour enfants de ${trancheDAge(s.age_min, s.age_max)} à ${commune(s)}, organisé par ${ORGANISATEUR}.`,
    /*
      Les dates sont des jours, pas des instants : `startDate` en `YYYY-MM-DD`
      est ce que schema.org attend pour un événement qui dure des journées
      entières, et cela évite d'inventer une heure qu'on ne connaît pas.
    */
    startDate: s.start_date,
    endDate: s.end_date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: s.centre,
      address: {
        '@type': 'PostalAddress',
        streetAddress: [s.address, s.address2].filter(Boolean).join(', ') || undefined,
        postalCode: s.postal_code ?? undefined,
        addressLocality: s.city ?? commune(s),
        addressCountry: 'BE',
      },
    },
    organizer: {
      '@type': 'Organization',
      name: ORGANISATEUR,
      url: 'https://www.eclosion.be',
    },
    audience: {
      '@type': 'PeopleAudience',
      suggestedMinAge: s.age_min,
      /* Exclue en base, donc l'âge maximal réellement accueilli est celui-ci. */
      suggestedMaxAge: s.age_max - 1,
    },
    image: s.image_url ?? undefined,
    url,
    offers: offres.length > 0 ? offres : undefined,
    isAccessibleForFree: false,
    inLanguage: 'fr-BE',
  };
}

/** Le fil d'Ariane, en JSON-LD (§19). */
export function filDArianeJsonLd(
  elements: { nom: string; chemin: string }[],
  origine: string,
): FilDArianeJsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: elements.map((e, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: e.nom,
      item: `${origine}${e.chemin}`,
    })),
  };
}
